/**
 * ZAPHUB - WebSocket Context Provider
 * Global WebSocket connection management with notification handling
 */

import React, { createContext, useContext, useCallback } from 'react';
import { useWebSocket } from '../hooks/useWebSocket';
import { toast } from 'sonner';

interface WebSocketMessage {
  type: 'notification' | 'workflow_update' | 'bonus_granted' | 'chat' | 'system';
  payload: any;
  timestamp: string;
}

interface WebSocketContextType {
  isConnected: boolean;
  lastMessage: WebSocketMessage | null;
  sendMessage: (message: any) => void;
  reconnect: () => void;
}

const WebSocketContext = createContext<WebSocketContextType | undefined>(undefined);

interface WebSocketProviderProps {
  children: React.ReactNode;
}

export function WebSocketProvider({ children }: WebSocketProviderProps) {
  const handleMessage = useCallback((message: WebSocketMessage) => {
    console.log('[WebSocket Context] Received message:', message);

    switch (message.type) {
      case 'notification':
        // Show toast notification
        toast(message.payload.title, {
          description: message.payload.message,
          duration: 5000
        });
        break;

      case 'workflow_update':
        const workflow = message.payload;

        toast.success(`Workflow ${workflow.status}`, {
          description: `"${workflow.name}" has been ${workflow.status}`,
          duration: 5000
        });
        break;

      case 'bonus_granted':
        const bonus = message.payload;

        toast.success('🎉 Bonus Granted!', {
          description: `You've earned $${bonus.amount} USD!`,
          duration: 7000
        });
        break;

      case 'chat':
        // Handle chat messages
        toast.info('New message', {
          description: message.payload.message,
          duration: 4000
        });
        break;

      case 'system':
        // Handle system messages
        console.log('[WebSocket] System message:', message.payload);
        break;

      default:
        console.warn('[WebSocket] Unknown message type:', message.type);
    }
  }, []);

  const handleConnect = useCallback(() => {
    console.log('[WebSocket Context] Connected to server');
    toast.success('Connected', {
      description: 'Real-time notifications enabled',
      duration: 2000
    });
  }, []);

  const handleDisconnect = useCallback(() => {
    console.log('[WebSocket Context] Disconnected from server');
    toast.warning('Disconnected', {
      description: 'Attempting to reconnect...',
      duration: 3000
    });
  }, []);

  const handleError = useCallback((error: Event) => {
    console.error('[WebSocket Context] Error:', error);
  }, []);

  const {
    isConnected,
    lastMessage,
    sendMessage,
    reconnect
  } = useWebSocket({
    autoConnect: true,
    reconnectInterval: 3000,
    maxReconnectAttempts: 10,
    onMessage: handleMessage,
    onConnect: handleConnect,
    onDisconnect: handleDisconnect,
    onError: handleError
  });

  const value: WebSocketContextType = {
    isConnected,
    lastMessage,
    sendMessage,
    reconnect
  };

  return (
    <WebSocketContext.Provider value={value}>
      {children}
    </WebSocketContext.Provider>
  );
}

export function useWebSocketContext(): WebSocketContextType {
  const context = useContext(WebSocketContext);
  if (!context) {
    throw new Error('useWebSocketContext must be used within WebSocketProvider');
  }
  return context;
}